import { useQuery } from "@tanstack/react-query"
import { Avatar, AvatarFallback, AvatarImage } from "design-system/atoms/Avatar"
import { Button } from "design-system/atoms/Button"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Card } from "design-system/molecules/Card"
import DateDisplay from "design-system/molecules/DateDisplay"
import { NoteComponentProps, NoteItem } from "design-system/organisms/chat/MessageList"
import { cn } from "design-system/utils"
import { Check, NotebookPen, Pencil, Trash2, X } from "lucide-react"
import { employeesQuery } from "modules/settings/lib/employeesQuery"
import { useState } from "react"
import { useMentionInput } from "../lib/useMentionInput"

function MentionTooltip({ employeeId, name }: { employeeId: string; name: string }) {
  const [open, setOpen] = useState(false)
  const { data: employees } = useQuery({ ...employeesQuery(), enabled: open })
  const employee = employees?.find((e) => e.id === employeeId)

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <span className="font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1 py-0.5 rounded cursor-pointer">
          {"@"}
          {name}
        </span>
      </TooltipTrigger>
      <TooltipContent variant="card" className="z-[9999] p-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 shrink-0">
            {employee?.avatar && <AvatarImage src={employee.avatar} />}
            <AvatarFallback className="text-xs">
              {(employee?.name ?? name)[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col text-left">
            <span className="text-sm font-medium">{employee?.name ?? name}</span>
            {employee?.email && (
              <span className="text-xs text-muted-foreground">{employee.email}</span>
            )}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function renderTextWithMentions(text: string, mentions: NoteItem["mentions"] = []) {
  const parts = text.split(/(@\{#\d+\})/g)
  return parts.map((part, i) => {
    const match = part.match(/^@\{#(\d+)\}$/)
    if (match) {
      const id = match[1]
      const mention = mentions.find((m) => m.id === id)
      if (mention && id) {
        return <MentionTooltip key={i} employeeId={id} name={mention.name} />
      }
    }
    return <span key={i}>{part}</span>
  })
}

export function Note({
  note,
  onEditNote,
  onDeleteNote,
  variant = "default",
}: NoteComponentProps & { variant?: "default" | "plain" }) {
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const mention = useMentionInput({ enabled: isEditing })

  const handleEditStart = () => {
    mention.setFromRaw(note.text, note.mentions ?? [])
    setIsEditing(true)
  }

  const handleEditCancel = () => {
    setIsEditing(false)
    mention.clear()
  }

  const handleEditSave = async () => {
    if (!onEditNote) return
    setIsSaving(true)
    try {
      await onEditNote(note.id, mention.rawValue)
      setIsEditing(false)
      mention.clear()
    } finally {
      setIsSaving(false)
    }
  }

  const isPlain = variant === "plain"

  return (
    <Card
      id={`note-${note.id}`}
      className={cn(
        "p-0 rounded-lg duration-200 border flex flex-col group/note",
        isPlain ? "border-border bg-card" : "border-warning/20 bg-warning/10",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between px-4",
          isPlain ? "bg-muted py-2" : "bg-warning/10 py-1",
        )}
      >
        <div className="flex gap-2 text-foreground/70">
          <NotebookPen size={16} />
          <span className="text-xs font-medium">{note.employee.name}</span>
        </div>
        <div className="flex gap-2 text-xs text-muted-foreground">
          {isEditing && (
            <div className="flex gap-1 justify-end">
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={handleEditCancel}
                disabled={isSaving}
                className="text-muted-foreground transition-colors !h-8 !w-8"
              >
                <X />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={handleEditSave}
                disabled={isSaving || mention.displayValue.trim().length === 0}
                className="!text-blue-600 disabled:opacity-40 transition-colors !h-8 !w-8"
              >
                <Check className="mt-0.5" />
              </Button>
            </div>
          )}
          {!isEditing && !isPlain && (
            <div className="flex gap-1 opacity-0 duration-200 group-hover/note:opacity-100">
              {onDeleteNote && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => onDeleteNote(note.id)}
                  className="text-muted-foreground hover:text-destructive transition-colors !w-8 !h-8"
                >
                  <Trash2 />
                </Button>
              )}
              {onEditNote && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={handleEditStart}
                  className="text-muted-foreground hover:text-foreground transition-colors !w-8 !h-8"
                >
                  <Pencil />
                </Button>
              )}
            </div>
          )}
          <DateDisplay date={note.createdAt} />
        </div>
      </div>
      <div className="px-4 py-4">
        {isEditing ? (
          <div className="relative">
            <Textarea
              ref={mention.textareaRef}
              value={mention.displayValue}
              {...mention.textareaHandlers}
              className="text-sm text-foreground bg-warning/5"
              autoFocus
            />
            {mention.dropdown}
          </div>
        ) : (
          <p className="text-sm text-foreground whitespace-pre-wrap italic">
            {renderTextWithMentions(note.text, note.mentions)}
          </p>
        )}
      </div>
    </Card>
  )
}
